import React from 'react';
import { Card } from 'react-bootstrap';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import { useTranslation } from 'react-i18next';

const INCOME_COLOR = 'var(--ev-action-primary-bg)';   // azul de marca — ventas
const EXPENSE_COLOR = 'var(--ev-danger-solid)';       // rojo — egresos (gastos)
const OTHER_INCOME_COLOR = 'var(--ev-info-solid)';    // otros ingresos (no ventas)

/**
 * SalesTrendChart - Área superpuesta de ventas, egresos (gastos) y otros ingresos (no ventas)
 * por día. "Otros ingresos" (préstamos, aportes…) es una serie propia: NO se suma a las ventas.
 * @param {Array} data - Array de objetos { date, total_sales, total_expenses, total_other_income }
 */
const SalesTrendChart = ({ data = [] }) => {
    const { t, i18n } = useTranslation();

    if (!data || data.length === 0) {
        return (
            <Card className="h-100">
                <Card.Body>
                    <Card.Title>{t('dashboard.incomeVsExpenses', 'Ingresos vs Egresos')}</Card.Title>
                    <p className="text-muted text-center py-5">
                        {t('dashboard.noDataAvailable')}
                    </p>
                </Card.Body>
            </Card>
        );
    }

    const formatCurrency = (value) => {
        return '$' + new Intl.NumberFormat(i18n.language, {
            style: 'decimal',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value || 0);
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr + 'T00:00:00');
        return new Intl.DateTimeFormat(i18n.language, {
            month: 'short',
            day: 'numeric'
        }).format(date);
    };

    const formatDateWithWeekday = (dateStr) => {
        const date = new Date(dateStr + 'T00:00:00');
        return new Intl.DateTimeFormat(i18n.language, {
            weekday: 'long',
            month: 'short',
            day: 'numeric'
        }).format(date);
    };

    const incomeLabel = t('dashboard.income', 'Ingresos');
    const expenseLabel = t('dashboard.expenses', 'Egresos');
    const otherIncomeLabel = t('dashboard.otherIncome', 'Otros ingresos');
    const hasOtherIncome = data.some((d) => Number(d.total_other_income) > 0);

    return (
        <Card className="h-100">
            <Card.Body>
                <Card.Title className="mb-4">
                    {t('dashboard.incomeVsExpenses', 'Ingresos vs Egresos')}
                </Card.Title>
                <div style={{ width: '100%', height: 300 }}>
                    <ResponsiveContainer>
                        <AreaChart
                            data={data}
                            margin={{
                                top: 10,
                                right: 30,
                                left: 0,
                                bottom: 0,
                            }}
                        >
                            <defs>
                                <linearGradient id="incomeGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={INCOME_COLOR} stopOpacity={0.35} />
                                    <stop offset="95%" stopColor={INCOME_COLOR} stopOpacity={0.03} />
                                </linearGradient>
                                <linearGradient id="expenseGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={EXPENSE_COLOR} stopOpacity={0.3} />
                                    <stop offset="95%" stopColor={EXPENSE_COLOR} stopOpacity={0.03} />
                                </linearGradient>
                                <linearGradient id="otherIncomeGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={OTHER_INCOME_COLOR} stopOpacity={0.3} />
                                    <stop offset="95%" stopColor={OTHER_INCOME_COLOR} stopOpacity={0.03} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis
                                dataKey="date"
                                tickFormatter={formatDate}
                                tick={{ fontSize: 12 }}
                                tickMargin={10}
                            />
                            <YAxis
                                tickFormatter={(val) => `$${val}`}
                                tick={{ fontSize: 12 }}
                            />
                            <Tooltip
                                formatter={(value, name) => [formatCurrency(value), name]}
                                labelFormatter={(label) => formatDateWithWeekday(label)}
                            />
                            <Legend />
                            <Area
                                type="monotone"
                                dataKey="total_sales"
                                name={incomeLabel}
                                stroke={INCOME_COLOR}
                                strokeWidth={2}
                                fill="url(#incomeGradient)"
                            />
                            <Area
                                type="monotone"
                                dataKey="total_expenses"
                                name={expenseLabel}
                                stroke={EXPENSE_COLOR}
                                strokeWidth={2}
                                fill="url(#expenseGradient)"
                            />
                            {hasOtherIncome && (
                                <Area
                                    type="monotone"
                                    dataKey="total_other_income"
                                    name={otherIncomeLabel}
                                    stroke={OTHER_INCOME_COLOR}
                                    strokeWidth={2}
                                    fill="url(#otherIncomeGradient)"
                                />
                            )}
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </Card.Body>
        </Card>
    );
};

export default SalesTrendChart;
